<!-- Component for displaying selected package details inside an overlay -->
<template>
	<div class="h-full w-full">
		<div class="flex h-full">
			<div
				class="h-full w-2/5 overflow-y-auto!"
				ref="scrollContainer"
			>
				<!-- Back arrow button -->
				<div
					class="sticky top-0 z-50 flex w-full items-center gap-1 bg-white px-8 py-4 transition-shadow duration-200"
					:class="{ 'shadow-md': isScrolled }"
				>
					<button
						type="button"
						@click="$emit('close-with-slide')"
					>
						<ArrowLeftCircleIcon
							class="hover:text-secondary-900 h-10 w-10 text-slate-700"
						/>
					</button>
					<span
						class="hover:text-secondary-900 text-sm font-medium tracking-wide text-slate-700 uppercase"
						>{{ t('general.all') }}</span
					>
				</div>
				<div class="flex w-full flex-col gap-5 p-8 pt-4">
					<div class="flex min-h-[48px] gap-6">
						<div class="ml-0.5 flex gap-2">
							<PlanAvatar
								v-for="(plan, index) in sessionStore.activePlans"
								:key="index"
								:index="index"
								class="mt-2"
								@click="toggleAvatarSelection(index)"
								:filled="isLineItemInPlan(pkg, plan)"
								:plan="plan"
							/>
						</div>

						<Transition
							enter-active-class="transition duration-300 ease-out"
							enter-from-class="-translate-x-32 opacity-0"
							enter-to-class="translate-x-0 opacity-100"
							leave-active-class="transition duration-300 ease-in"
							leave-from-class="opacity-100"
							leave-to-class="opacity-0"
						>
							<div
								v-if="showNoPackageAlert"
								class="bg-accent-100 pointer-events-auto relative flex transform flex-row items-center justify-between rounded-md p-3 text-black shadow-sm"
							>
								<p class="font-medium">
									{{ t('packageSlide.additionPrompt') }}
								</p>

								<!-- Left-pointing arrow -->
								<div
									class="bg-accent-100 absolute top-1/2 left-0 z-10 -ml-2 h-4 w-4 -translate-y-1/2 rotate-45 transform"
								></div>
							</div>
						</Transition>
					</div>

					<div class="flex flex-col space-y-6">
						<h2 class="text-secondary-900 font-serif text-6xl">
							{{ pkg.name }}
						</h2>
						<div class="text-primary-700 w-full text-4xl font-bold">
							{{ formatPrice(totalPrice) }}
						</div>

						<div>
							<!-- Package Line Items -->
							<ul class="flex w-full flex-col gap-2">
								<li
									v-for="item in displayLineItems"
									:key="item.type === 'oap' ? 'oap' : item.lineItem.id"
									class="bg-background-50 rounded-md px-4 py-3"
								>
									<PackageOapItem
										v-if="item.type === 'oap'"
										v-model="hasOapLocally"
										:pkg="pkg"
										class="bg-background-50 rounded-md"
									/>
									<div
										v-else
										class="flex items-center gap-2"
									>
										<component
											:is="
												item.lineItem.isGuaranteed
													? ShieldCheckIcon
													: CheckCircleIcon
											"
											class="text-accent-600 h-5 w-5 min-w-5"
										/>
										<span class="text-secondary-900 text-sm font-medium">
											{{ item.lineItem.name }}
										</span>
									</div>
								</li>

								<!-- Toggle button -->
								<li
									v-if="hasHiddenItems"
									class="cursor-pointer"
								>
									<div
										class="my-3 flex w-fit items-center"
										@click="showAllItems = !showAllItems"
									>
										<component
											:is="showAllItems ? MinusCircleIcon : PlusCircleIcon"
											class="text-secondary-300 h-6 w-6"
										/>
										<span
											class="text-secondary-400 ml-2 font-bold tracking-tight"
										>
											{{
												showAllItems
													? t('merchandise.less')
													: t('merchandise.more')
											}}
										</span>
									</div>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>

			<!--Package Image-->
			<div class="w-3/5">
				<div
					class="relative aspect-435/220 h-full w-full overflow-hidden md:aspect-435/360"
				>
					<div
						class="h-full w-full bg-cover bg-center bg-no-repeat"
						:style="`background-image: url(${imageUrl});`"
					></div>
				</div>
			</div>
		</div>

		<!-- Advance Button -->
		<Transition name="jiggle">
			<button
				v-if="advanceButtonText"
				type="button"
				@click="onAdvanceButtonClick"
				data-test-id="advance-button"
				class="bg-accent-400 text-accent-700 hover:bg-accent-700 absolute right-8 bottom-8 flex flex-row items-center gap-2 rounded-lg px-10 py-2 font-sans text-base leading-loose font-bold hover:text-white"
				:class="shouldJiggle ? 'animate-jiggle' : ''"
			>
				{{ advanceButtonText }} <ArrowLongRightIcon class="size-6" />
			</button>
		</Transition>
	</div>
</template>

<script setup lang="ts">
import PackageOapItem from '../merchandise/PackageOapItem.vue'
import type { PbLineItem, PbLineItemPackage } from '@/apiTypes'
import PlanAvatar from '@/components/presentation/PlanAvatar.vue'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { isProductsSlideStoryblok } from '@/guards'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import type { AssetStoryblok, PackagesSlideStoryblok } from '@/sbTypes'
import { useAssetStore } from '@/stores/asset'
import { usePresentationController } from '@/stores/presentationController'
import { useSessionStore } from '@/stores/session'
import { ArrowLongRightIcon } from '@heroicons/vue/24/solid'
import ArrowLeftCircleIcon from '@heroicons/vue/24/solid/ArrowLeftCircleIcon'
import CheckCircleIcon from '@heroicons/vue/24/solid/CheckCircleIcon'
import MinusCircleIcon from '@heroicons/vue/24/solid/MinusCircleIcon'
import PlusCircleIcon from '@heroicons/vue/24/solid/PlusCircleIcon'
import ShieldCheckIcon from '@heroicons/vue/24/solid/ShieldCheckIcon'
import { computedAsync } from '@vueuse/core'
import {
	computed,
	onBeforeUnmount,
	onMounted,
	type PropType,
	ref,
	watch,
} from 'vue'

const props = defineProps({
	pkg: {
		type: Object as PropType<PbLineItemPackage>,
		required: true,
	},
	blok: Object as PropType<PackagesSlideStoryblok>,
})

const { t } = usePresentationLocale()

const {
	priceBook,
	isLineItemInPlan,
	mockProductOap,
	formatPrice,
	calculatePackageTotal,
} = useMerchandise()

const asset = useAssetStore()

const presentation = usePresentationController()

const scrollContainer = ref<HTMLElement | null>(null)

const isScrolled = ref(false)

const sessionStore = useSessionStore()

const showAllItems = ref(false)

const advanceAttempted = ref(false)

const shouldJiggle = ref(false)

const showNoPackageAlert = computed(
	() => advanceAttempted.value && selectedPlans.value.length < 1
)

/** Advance button click handler */
function onAdvanceButtonClick() {
	if (selectedPlans.value.length > 0 || advanceAttempted.value) {
		handleAdvance()
	} else {
		advanceAttempted.value = true

		if (!shouldJiggle.value && showNoPackageAlert.value) {
			shouldJiggle.value = true
		}
	}
}

/** The text to show on the advance button */
const advanceButtonText = computed(() => {
	if (props.pkg.subcategory === PbSubcategory.Cremation) {
		return props.blok?.cremationAdvanceButtonText
	} else {
		return props.blok?.burialAdvanceButtonText
	}
})

// Get plans
const plans = computed(() => sessionStore.activePlans ?? [])

// Get all line items in the package except for transportation transfer
const packageLineItems = computed(
	() =>
		priceBook.value?.getSortedPackageProducts(
			priceBook.value.getProductsInPackage(props.pkg.id) ?? [],
			true
		) ?? []
)

type DisplayItem = { type: 'lineItem'; lineItem: PbLineItem } | { type: 'oap' }

/**
 * Create an array of line items to display. If showAllItems is false,
 * only show the first 5 items and the OAP item at the bottom. If
 * showAllItems is true, show all additional items after oap.
 */
const displayLineItems = computed<DisplayItem[]>(() => {
	const items = packageLineItems.value.flatMap((pkgProduct) => {
		const lineItem = priceBook.value?.findLineItem(pkgProduct.refSpbLineProduct)
		return lineItem ? [{ type: 'lineItem' as const, lineItem }] : []
	})

	const oapItem = mockProductOap.value ? [{ type: 'oap' as const }] : []
	const firstFive = items.slice(0, 5)
	const remaining = items.slice(5)

	return showAllItems.value
		? [...firstFive, ...oapItem, ...remaining]
		: [...firstFive, ...oapItem]
})

// Check if there are more than 5 items
const hasHiddenItems = computed(() => packageLineItems.value.length > 5)

/**
 * Updates the isScrolled state based on the scroll position of scrollContainer.
 * Sets isScrolled to true if the scroll position is greater than 0, indicating
 * that the container has been scrolled, otherwise sets it to false.
 */
const handleScroll = () => {
	if (!scrollContainer.value) return
	isScrolled.value = scrollContainer.value.scrollTop > 0
}

/**
 * Add a scroll event listener to scrollContainer when the component is mounted.
 */
onMounted(() => {
	scrollContainer.value?.addEventListener('scroll', handleScroll)
})

/**
 * Remove the scroll event listener from scrollContainer when the component is unmounted.
 */
onBeforeUnmount(() => {
	scrollContainer.value?.removeEventListener('scroll', handleScroll)
})

// Emit an event to close the overlay
const emit = defineEmits<{
	(event: 'close'): void
	(event: 'close-with-slide'): void
}>()

/**
 * Compute the total price for the package.
 * If no plans are selected, add the OAP price to the base.
 * If OAP is locally included, add the OAP price to the base.
 * Otherwise, return the base price only.
 */
const totalPrice = computed(() => {
	const base = calculatePackageTotal(props.pkg.id)
	const oap = mockProductOap.value?.fixedPrice ?? 0

	if (selectedPlans.value.length === 0 || hasOapLocally.value) {
		return base + oap
	}
	return base
})

watch(
	() => presentation.activeSlideId,
	() => {
		emit('close')
	}
)

onMounted(() => {
	const presentation = usePresentationController()

	presentation.setSlideNavigationGuard(() => {
		if (selectedPlans.value.length > 0) {
			// Package is already selected: allow navigation
			advanceAttempted.value = false
			return true
		}

		if (advanceAttempted.value) {
			// Second navigation attempt: allow navigation
			advanceAttempted.value = false
			return true
		}

		// First click attempt without a selection: block navigation
		advanceAttempted.value = true
		return false
	})
})

// Remove navigation guard
onBeforeUnmount(() => {
	presentation.setSlideNavigationGuard(null)
})

// Get the image blob url
const imageUrl = computedAsync(() =>
	asset.getImageBlobUrl({
		filename: props.pkg.productImageUrl,
		name: props.pkg.name,
	} as AssetStoryblok)
)

// Get selected plans
const selectedPlans = computed(() =>
	plans.value.filter((plan) =>
		plan.cartItems.some((item) => item.lineItemId === props.pkg.id)
	)
)

/* Track if the OAP is included in any selected plan and set hasOapLocally accordingly
 * if no plans are selected, set hasOapLocally to true by default
 */
const hasOapLocally = computed({
	get: () => {
		return selectedPlans.value.length === 0
			? true
			: selectedPlans.value.some((plan) => plan.oapIncluded)
	},
	set: (newVal: boolean) => {
		selectedPlans.value.forEach((plan) => {
			plan.oapIncluded = newVal
		})
	},
})

// Toggle package selection for a plan
const toggleAvatarSelection = (index: number) => {
	const plan = plans.value[index]
	if (!plan || !priceBook.value) return

	const subItems = priceBook.value.getProductsInPackage(props.pkg.id)
	if (!subItems) {
		throw new Error('Package line items not found')
	}

	plan.togglePackage(props.pkg, subItems, priceBook.value.packages)
}

/** Go to the slide for urns or caskets, depending on the package we're looking at */
function handleAdvance() {
	let targetSlideProduct: string
	const controller = usePresentationController()

	// starting from PSS-1509: we will always start on a casket slide
	targetSlideProduct = 'casket'

	const productSlide = controller.allSlides?.find(
		(slide) =>
			isProductsSlideStoryblok(slide) &&
			slide.productType === targetSlideProduct
	)

	if (!productSlide) {
		throw new Error('Could not find product slide for ' + targetSlideProduct)
	}

	controller.gotoSlideId(productSlide._uid)
}
</script>

<style scoped>
.animate-jiggle {
	animation: jiggle 0.7s ease;
}

@keyframes jiggle {
	0%,
	100% {
		transform: translateX(0);
	}
	20% {
		transform: translateX(-6px);
	}
	40% {
		transform: translateX(6px);
	}
	60% {
		transform: translateX(-4px);
	}
	80% {
		transform: translateX(4px);
	}
}
</style>
